import { useCallback, useEffect, useRef, Component, ReactNode } from 'react';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  MapMouseEvent,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';

class MapErrorBoundary extends Component<{ children: ReactNode; fallback: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error) { console.error('Google Maps failed to load:', error); }
  render() { return this.state.hasError ? this.props.fallback : this.props.children; }
}

interface MapPickerProps {
  latitude?: number | null;
  longitude?: number | null;
  onChange: (lat: number, lng: number) => void;
  className?: string;
  searchPlaceholder?: string;
}

const DUBAI_CENTER = { lat: 25.2, lng: 55.27 };
const DEFAULT_ZOOM = 12;
const SELECTED_ZOOM = 16;

interface PlaceSearchBoxProps {
  onPlaceSelected: (lat: number, lng: number) => void;
  placeholder: string;
}

function PlaceSearchBox({ onPlaceSelected, placeholder }: PlaceSearchBoxProps) {
  const placesLib = useMapsLibrary('places');
  const map = useMap();
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!placesLib || !inputRef.current) return;

    const autocomplete = new placesLib.Autocomplete(inputRef.current, {
      fields: ['geometry', 'name', 'formatted_address'],
      componentRestrictions: { country: 'ae' },
    });

    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      const loc = place.geometry?.location;
      if (!loc) return;
      const lat = loc.lat();
      const lng = loc.lng();
      onPlaceSelected(lat, lng);
      if (map) {
        map.panTo({ lat, lng });
        map.setZoom(SELECTED_ZOOM);
      }
    });

    return () => listener.remove();
  }, [placesLib, map, onPlaceSelected]);

  return (
    <input
      ref={inputRef}
      type="text"
      className="map-search-input"
      placeholder={placeholder}
      onKeyDown={(e) => {
        // Prevent form submission when picking a suggestion with Enter.
        if (e.key === 'Enter') e.preventDefault();
      }}
    />
  );
}

export default function MapPicker({
  latitude,
  longitude,
  onChange,
  className,
  searchPlaceholder,
}: MapPickerProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  const handleMapClick = useCallback(
    (event: MapMouseEvent) => {
      const latLng = event.detail.latLng;
      if (latLng) {
        onChange(latLng.lat, latLng.lng);
      }
    },
    [onChange],
  );

  if (!apiKey) {
    return (
      <div className={`map-picker-placeholder ${className ?? ''}`}>
        Google Maps API key is not configured
      </div>
    );
  }

  const hasMarker =
    latitude !== undefined &&
    latitude !== null &&
    longitude !== undefined &&
    longitude !== null;

  const center = hasMarker
    ? { lat: latitude, lng: longitude }
    : DUBAI_CENTER;

  const fallbackEl = (
    <div className={`map-picker-placeholder ${className ?? ''}`}>
      Map is temporarily unavailable. You can still select City and Area from the dropdowns.
    </div>
  );

  return (
    <MapErrorBoundary fallback={fallbackEl}>
      <APIProvider apiKey={apiKey}>
        <div className={`map-picker-search-wrapper ${className ?? ''}`}>
          <PlaceSearchBox
            onPlaceSelected={onChange}
            placeholder={searchPlaceholder ?? 'Search for a place, mall, or street'}
          />
        </div>
        <div className={`map-picker-container ${className ?? ''}`}>
          <Map
            defaultCenter={center}
            defaultZoom={DEFAULT_ZOOM}
            gestureHandling="greedy"
            disableDefaultUI={false}
            mapId="garagat-map-picker"
            onClick={handleMapClick}
            style={{ width: '100%', height: '100%' }}
          >
            {hasMarker && (
              <AdvancedMarker position={{ lat: latitude, lng: longitude }} />
            )}
          </Map>
        </div>
      </APIProvider>
    </MapErrorBoundary>
  );
}
